import { Component, OnInit, OnDestroy, ChangeDetectorRef, HostListener } from '@angular/core';
import { Router } from '@angular/router';
import { Subscription } from 'rxjs';
import { FloatingThreadService, FloatingThreadState } from '../../../services/floating-thread.service';
import { AuthService } from '../../../services/auth.service';
import { ThemeService } from '../../../services/theme.service';
import { getAgentName, getAgentRoleLabel } from 'src/app/data/agent-catalog';

@Component({
  selector: 'app-floating-thread-container',
  templateUrl: './floating-thread-container.component.html',
  styleUrl: './floating-thread-container.component.scss',
  host: {
    '[class.theme-light]': 'theme.isLight',
    '[class.has-pinned-thread]': 'hasPinnedVisibleThread()',
    '[class.reserve-space]': 'shouldReservePinnedSpace()',
    '[class.resizing-pinned-rail]': 'isResizingPinnedRail',
    '[style.--pinned-thread-width]': 'pinnedWidthCss'
  }
})
export class FloatingThreadContainerComponent implements OnInit, OnDestroy {
  floatingThreads: FloatingThreadState[] = [];
  private subscription: Subscription = new Subscription();
  private previousThreadIds: Set<string> = new Set(); // Track existing threads
  readonly pinnedWidthCss: string;
  pinnedWidth = 440;
  isResizingPinnedRail = false;
  private pinnedResizeStartX = 0;
  private pinnedResizeStartWidth = 0;
  private readonly PINNED_WIDTH_STORAGE_KEY = 'floating-thread-pinned-width';
  private readonly MIN_PINNED_WIDTH = 300;
  private readonly MAX_PINNED_WIDTH = 720;

  // Drag state
  private isDragging = false;
  private dragStartPos = { x: 0, y: 0 };
  private dragStartPanelPos = { x: 0, y: 0 };
  private currentDragThreadId: string | null = null;

  // Resize state
  private isResizing = false;
  private resizeStartPos = { x: 0, y: 0 };
  private resizeStartSize = { width: 0, height: 0 };
  private resizeStartPosition = { x: 0, y: 0 };
  private currentResizeThreadId: string | null = null;
  private resizeDirection = '';

  // Performance optimization - batch updates only during drag/resize
  private lastUpdateTime = 0;
  private readonly UPDATE_THROTTLE = 16; // ~60fps
  private pendingUpdate: any = null;
  private isDuringInteraction = false; // Track if we're currently dragging/resizing

  // For demo purposes - hardcoded thread data
  mockThreadData: { [key: string]: any } = {
    'thread-1': {
      id: 'thread-1',
      name: 'Marketing Strategy Discussion',
      agentType: 'marketing',
      status: 'active',
      updatedAt: new Date()
    },
    'thread-2': {
      id: 'thread-2', 
      name: 'Customer Support Issue',
      agentType: 'customer-success-manager',
      status: 'waiting_for_user',
      updatedAt: new Date()
    },
    'thread-3': {
      id: 'thread-3',
      name: 'Sales Pipeline Review',
      agentType: 'sdr',
      status: 'running',
      updatedAt: new Date()
    }
  };

  constructor(
    private floatingThreadService: FloatingThreadService,
    private cdr: ChangeDetectorRef,
    public theme: ThemeService,
    private router: Router,
    private authService: AuthService
  ) {
    this.pinnedWidth = this.getInitialPinnedWidth();
    this.pinnedWidthCss = `${this.pinnedWidth}px`;
  }

  ngOnInit(): void {
    // Subscribe to floating threads changes - keep it simple and functional
    this.subscription.add(
      this.floatingThreadService.floatingThreads$.subscribe(threads => {
        this.floatingThreads = threads;
        this.syncPinnedWidthCss();
        // Let Angular handle change detection normally
      })
    );
  }

  ngOnDestroy(): void {
    this.subscription.unsubscribe();
    // Clean up any pending updates
    if (this.pendingUpdate) {
      cancelAnimationFrame(this.pendingUpdate);
    }
  }

  /**
   * Handle thread panel close
   */
  onCloseThread(threadId: string): void {
    this.floatingThreadService.closeThread(threadId);
  }

  /**
   * Handle thread panel minimize/maximize toggle
   */
  onToggleMinimize(threadId: string): void {
    // The service now handles restoring from maximized state before minimizing
    this.floatingThreadService.toggleMinimize(threadId);
  }

  /**
   * Handle thread panel maximize/restore toggle
   */
  onToggleMaximize(threadId: string): void {
    this.floatingThreadService.toggleMaximize(threadId);
  }

  onTogglePin(threadId: string): void {
    this.floatingThreadService.togglePin(threadId);
  }

  onShowAsFloating(threadId: string): void {
    this.floatingThreadService.showThread(threadId, 'floating');
  }

  onOpenThreadPage(threadId: string): void {
    const currentUrl = this.router.url;
    const returnTo = currentUrl.startsWith('/myagency/agents/') ? this.getDefaultReturnUrl() : currentUrl;
    const queryParams: Record<string, string> = {};

    if (this.authService.aid) {
      queryParams['uid'] = this.authService.aid;
    }

    if (returnTo) {
      queryParams['returnTo'] = returnTo;
    }

    this.floatingThreadService.closeThread(threadId);
    this.router.navigate(['/myagency/agents', threadId], { queryParams });
  }

  onPinnedResizeStart(event: MouseEvent): void {
    if (!this.shouldReservePinnedSpace()) {
      return;
    }

    event.preventDefault();
    event.stopPropagation();

    this.isResizingPinnedRail = true;
    this.isDuringInteraction = true;
    this.pinnedResizeStartX = event.clientX;
    this.pinnedResizeStartWidth = this.pinnedWidth;

    this.addGlobalDragListeners();
  }

  /**
   * Check if any thread is currently maximized
   */
  hasMaximizedThread(): boolean {
    return this.floatingThreads.some(thread => thread.isMaximized);
  }

  /**
   * Handle backdrop click to restore maximized thread
   */
  onBackdropClick(): void {
    const maximizedThread = this.floatingThreads.find(thread => thread.isMaximized);
    if (maximizedThread) {
      this.onToggleMaximize(maximizedThread.threadId);
    }
  }

  hasPinnedVisibleThread(): boolean {
    return this.floatingThreads.some(thread => thread.displayMode === 'pinned' && !thread.isMinimized && !thread.isMaximized);
  }

  shouldReservePinnedSpace(): boolean {
    return this.hasPinnedVisibleThread() && !this.hasMaximizedThread();
  }

  getPinnedThread(): FloatingThreadState | null {
    return this.floatingThreads.find(thread => thread.displayMode === 'pinned' && !thread.isMinimized && !thread.isMaximized) || null;
  }

  getOverlayThreads(): FloatingThreadState[] {
    return this.floatingThreads.filter(thread => thread.displayMode !== 'pinned' && !thread.isMinimized);
  }

  isPinnedThread(thread: FloatingThreadState): boolean {
    return thread.displayMode === 'pinned' && !thread.isMaximized;
  }

  canPinThread(thread: FloatingThreadState): boolean {
    return typeof window !== 'undefined' && window.innerWidth > 768;
  }

  getThreadTitle(threadId: string): string {
    return 'Agent';
  }

  @HostListener('window:resize')
  onWindowResize(): void {
    const nextWidth = this.getClampedPinnedWidth(this.pinnedWidth);
    if (nextWidth !== this.pinnedWidth) {
      this.pinnedWidth = nextWidth;
      this.syncPinnedWidthCss();
    }
  }

  /**
   * Handle thread panel focus (bring to front)
   */
  onFocusThread(threadId: string): void {
    this.floatingThreadService.focusThread(threadId);
  }

  /**
   * Handle keyboard shortcuts for focused thread
   */
  @HostListener('document:keydown', ['$event'])
  handleKeyboardShortcuts(event: KeyboardEvent): void {
    // Check if an input field is currently focused
    const activeElement = document.activeElement;
    const isInputFocused = activeElement && (
      activeElement.tagName === 'INPUT' ||
      activeElement.tagName === 'TEXTAREA' ||
      activeElement.tagName === 'SELECT' ||
      activeElement.hasAttribute('contenteditable') ||
      activeElement.getAttribute('role') === 'textbox'
    );

    // Skip keyboard shortcuts if an input is focused
    if (isInputFocused) {
      return;
    }

    // Only handle shortcuts if we have visible floating threads
    const visibleThreads = this.floatingThreads.filter(thread => !thread.isMinimized);
    if (visibleThreads.length === 0) {
      return;
    }

    // Find the currently focused thread (highest zIndex)
    const focusedThread = visibleThreads.reduce((prev, current) => 
      (current.zIndex > prev.zIndex) ? current : prev
    );

    if (!focusedThread) {
      return;
    }

    // Escape: Close the focused thread
    if (event.key === 'Escape' && !event.shiftKey) {
      event.preventDefault();
      this.onCloseThread(focusedThread.threadId);
    }

    // Shift + Escape: Minimize the focused thread
    if (event.key === 'Escape' && event.shiftKey) {
      event.preventDefault();
      this.onToggleMinimize(focusedThread.threadId);
    }
  }

  /**
   * Get mock thread data for display
   */
  getThreadData(threadId: string): any {
    return this.mockThreadData[threadId] || {
      id: threadId,
      name: 'Unknown Thread',
      agentType: 'sdr',
      status: 'active',
      updatedAt: new Date()
    };
  }

  /**
   * Get agent type display name
   */
  getAgentDisplayName(agentType: string): string {
    return getAgentName(agentType, agentType);
  }

  /**
   * Get status color class
   */
  getStatusClass(status: string): string {
    const statusClasses: { [key: string]: string } = {
      'active': 'status-active',
      'completed': 'status-completed',
      'running': 'status-running',
      'stopped': 'status-stopped',
      'waiting_for_user': 'status-waiting'
    };
    return statusClasses[status] || 'status-active';
  }

  /**
   * Get context UID for a thread
   */
  getContextUid(threadId: string): string {
    // For now, return a default context UID
    // This would typically be derived from the thread data or service
    return this.mockThreadData[threadId]?.contextUid || '';
  }

  /**
   * Get agency ID for a thread
   */
  getAgencyId(threadId: string): string {
    // For now, return a default agency ID
    // This would typically be derived from the thread data or service
    return this.mockThreadData[threadId]?.agencyId || '';
  }

  /**
   * Get context for a thread
   */
  getContext(threadId: string): string {
    // Return context based on thread type or data
    const threadData = this.mockThreadData[threadId];
    if (!threadData) return 'general assistance';
    
    // Generate context based on agent type
    return `${getAgentRoleLabel(threadData.agentType, 'general assistance').toLowerCase()} work`;
  }

  /**
   * Get agent type for a thread
   */
  getAgent(threadId: string): string {
    const threadData = this.mockThreadData[threadId];
    return threadData?.agentType || 'sdr';
  }

  /**
   * Get context type for a thread
   */
  getContextType(threadId: string): 'user' | 'agency' | 'business' | 'affiliate' {
    // For now, default to 'business' but this could be configured per thread
    return this.mockThreadData[threadId]?.contextType || 'business';
  }

  private getDefaultReturnUrl(): string {
    return '/myagency';
  }

  // =============
  // DRAG FUNCTIONALITY
  // =============

  /**
   * Start dragging a panel
   */
  onDragStart(event: MouseEvent, threadId: string): void {
    if (this.isResizing) return;
    
    // Don't allow dragging maximized threads
    const thread = this.floatingThreads.find(t => t.threadId === threadId);
    if (thread?.isMaximized || thread?.displayMode === 'pinned') return;
    
    event.preventDefault();
    event.stopPropagation();
    
    this.isDragging = true;
    this.isDuringInteraction = true;
    this.currentDragThreadId = threadId;
    this.dragStartPos = { x: event.clientX, y: event.clientY };
    
    if (thread) {
      this.dragStartPanelPos = { x: thread.position.x, y: thread.position.y };
      this.onFocusThread(threadId);
    }

    this.addGlobalDragListeners();
  }

  /**
   * Handle panel dragging
   */
  private onGlobalMouseMove = (event: MouseEvent): void => {
    if (this.isResizingPinnedRail) {
      this.handlePinnedResizeMove(event);
    } else if (this.isDragging && this.currentDragThreadId) {
      this.handleDragMove(event);
    } else if (this.isResizing && this.currentResizeThreadId) {
      this.handleResizeMove(event);
    }
  }

  private handlePinnedResizeMove(event: MouseEvent): void {
    const proposedWidth = this.pinnedResizeStartWidth + (this.pinnedResizeStartX - event.clientX);
    const nextWidth = this.getClampedPinnedWidth(proposedWidth);

    if (nextWidth !== this.pinnedWidth) {
      this.pinnedWidth = nextWidth;
      this.syncPinnedWidthCss();
    }
  }

  /**
   * Handle drag movement with throttling
   */
  private handleDragMove(event: MouseEvent): void {
    // Only throttle during interaction - always perform update immediately otherwise
    if (!this.isDuringInteraction) {
      this.performDragUpdate(event);
      return;
    }
    
    // Throttle updates for performance during interaction
    const now = Date.now();
    if (now - this.lastUpdateTime < this.UPDATE_THROTTLE) {
      if (this.pendingUpdate) {
        cancelAnimationFrame(this.pendingUpdate);
      }
      this.pendingUpdate = requestAnimationFrame(() => this.performDragUpdate(event));
      return;
    }
    
    this.performDragUpdate(event);
  }

  /**
   * Perform the actual drag update
   */
  private performDragUpdate(event: MouseEvent): void {
    this.lastUpdateTime = Date.now();
    this.pendingUpdate = null;
    
    const deltaX = event.clientX - this.dragStartPos.x;
    const deltaY = event.clientY - this.dragStartPos.y;
    
    // Get the current thread to access its dimensions
    const currentThread = this.floatingThreads.find(t => t.threadId === this.currentDragThreadId);
    if (!currentThread) return;
    
    // Calculate viewport boundaries
    const viewportWidth = window.innerWidth;
    const viewportHeight = window.innerHeight;
    
    // Calculate new position with constraints
    let newX = this.dragStartPanelPos.x + deltaX;
    let newY = this.dragStartPanelPos.y + deltaY;
    
    // Enforce left and top boundaries (minimum 0)
    newX = Math.max(0, newX);
    newY = Math.max(0, newY);
    
    // Enforce right and bottom boundaries
    // Account for panel width and height to keep entire panel visible
    const maxX = viewportWidth - currentThread.size.width;
    const maxY = viewportHeight - currentThread.size.height;
    
    newX = Math.min(maxX, newX);
    newY = Math.min(maxY, newY);
    
    this.floatingThreadService.updateThreadPosition(
      this.currentDragThreadId!,
      { x: newX, y: newY }
    );
  }

  /**
   * End dragging
   */
  private onGlobalMouseUp = (): void => {
    if (this.isResizingPinnedRail) {
      this.isResizingPinnedRail = false;
      this.isDuringInteraction = false;
      this.persistPinnedWidth();
      this.removeGlobalDragListeners();
    } else if (this.isDragging) {
      this.isDragging = false;
      this.isDuringInteraction = false;
      this.currentDragThreadId = null;
      this.removeGlobalDragListeners();
    } else if (this.isResizing) {
      this.isResizing = false;
      this.isDuringInteraction = false;
      this.currentResizeThreadId = null;
      this.resizeDirection = '';
      this.removeGlobalDragListeners();
    }
    
    // Clean up any pending updates
    if (this.pendingUpdate) {
      cancelAnimationFrame(this.pendingUpdate);
      this.pendingUpdate = null;
    }
  }

  // =============
  // RESIZE FUNCTIONALITY
  // =============

  /**
   * Start resizing a panel
   */
  onResizeStart(event: MouseEvent, threadId: string, direction: string): void {
    if (this.isDragging) return;
    
    // Don't allow resizing maximized threads
    const thread = this.floatingThreads.find(t => t.threadId === threadId);
    if (thread?.isMaximized || thread?.displayMode === 'pinned') return;
    
    event.preventDefault();
    event.stopPropagation();
    
    this.isResizing = true;
    this.isDuringInteraction = true;
    this.currentResizeThreadId = threadId;
    this.resizeDirection = direction;
    this.resizeStartPos = { x: event.clientX, y: event.clientY };
    
    if (thread) {
      this.resizeStartSize = { width: thread.size.width, height: thread.size.height };
      this.resizeStartPosition = { x: thread.position.x, y: thread.position.y };
      this.onFocusThread(threadId);
    }

    this.addGlobalDragListeners();
  }

  /**
   * Handle resize movement with throttling
   */
  private handleResizeMove(event: MouseEvent): void {
    // Only throttle during interaction - always perform update immediately otherwise
    if (!this.isDuringInteraction) {
      this.performResizeUpdate(event);
      return;
    }
    
    // Throttle updates for performance during interaction
    const now = Date.now();
    if (now - this.lastUpdateTime < this.UPDATE_THROTTLE) {
      if (this.pendingUpdate) {
        cancelAnimationFrame(this.pendingUpdate);
      }
      this.pendingUpdate = requestAnimationFrame(() => this.performResizeUpdate(event));
      return;
    }
    
    this.performResizeUpdate(event);
  }

  /**
   * Perform the actual resize update
   */
  private performResizeUpdate(event: MouseEvent): void {
    this.lastUpdateTime = Date.now();
    this.pendingUpdate = null;
    
    const deltaX = event.clientX - this.resizeStartPos.x;
    const deltaY = event.clientY - this.resizeStartPos.y;
    
    // Get viewport boundaries
    const viewportWidth = window.innerWidth;
    const viewportHeight = window.innerHeight;
    
    let newWidth = this.resizeStartSize.width;
    let newHeight = this.resizeStartSize.height;
    let newX = this.resizeStartPosition.x;
    let newY = this.resizeStartPosition.y;
    
    // Calculate new dimensions and positions based on resize direction
    if (this.resizeDirection.includes('e')) {
      // East (right) - only change width, constrain to viewport right edge
      const maxWidth = viewportWidth - this.resizeStartPosition.x;
      newWidth = Math.max(250, Math.min(maxWidth, this.resizeStartSize.width + deltaX));
    }
    if (this.resizeDirection.includes('w')) {
      // West (left) - change width and x position, constrain to viewport left edge
      const widthChange = -deltaX;
      const proposedWidth = this.resizeStartSize.width + widthChange;
      const proposedX = this.resizeStartPosition.x - (proposedWidth - this.resizeStartSize.width);
      
      // Ensure we don't go past left edge and maintain minimum width
      if (proposedX >= 0 && proposedWidth >= 250) {
        newWidth = proposedWidth;
        newX = proposedX;
      } else if (proposedX < 0) {
        // Hit left edge - set position to 0 and calculate width accordingly
        newX = 0;
        newWidth = this.resizeStartPosition.x + this.resizeStartSize.width;
      } else {
        // Hit minimum width constraint
        newWidth = 250;
        newX = this.resizeStartPosition.x + this.resizeStartSize.width - 250;
      }
    }
    if (this.resizeDirection.includes('s')) {
      // South (bottom) - only change height, constrain to viewport bottom edge
      const maxHeight = viewportHeight - this.resizeStartPosition.y;
      newHeight = Math.max(200, Math.min(maxHeight, this.resizeStartSize.height + deltaY));
    }
    if (this.resizeDirection.includes('n')) {
      // North (top) - change height and y position, constrain to viewport top edge
      const heightChange = -deltaY;
      const proposedHeight = this.resizeStartSize.height + heightChange;
      const proposedY = this.resizeStartPosition.y - (proposedHeight - this.resizeStartSize.height);
      
      // Ensure we don't go past top edge and maintain minimum height
      if (proposedY >= 0 && proposedHeight >= 200) {
        newHeight = proposedHeight;
        newY = proposedY;
      } else if (proposedY < 0) {
        // Hit top edge - set position to 0 and calculate height accordingly
        newY = 0;
        newHeight = this.resizeStartPosition.y + this.resizeStartSize.height;
      } else {
        // Hit minimum height constraint
        newHeight = 200;
        newY = this.resizeStartPosition.y + this.resizeStartSize.height - 200;
      }
    }
    
    // Update both size and position
    this.floatingThreadService.updateThreadSize(
      this.currentResizeThreadId!,
      { width: newWidth, height: newHeight }
    );
    
    // Only update position if it changed (for west and north resizing)
    if (newX !== this.resizeStartPosition.x || newY !== this.resizeStartPosition.y) {
      this.floatingThreadService.updateThreadPosition(
        this.currentResizeThreadId!,
        { x: newX, y: newY }
      );
    }
  }

  /**
   * Add global mouse event listeners
   */
  private addGlobalDragListeners(): void {
    if (typeof document !== 'undefined') {
      document.addEventListener('mousemove', this.onGlobalMouseMove);
      document.addEventListener('mouseup', this.onGlobalMouseUp);
      document.body.style.userSelect = 'none';
      document.body.style.cursor = this.getInteractionCursor();
    }
  }

  /**
   * Remove global mouse event listeners
   */
  private removeGlobalDragListeners(): void {
    if (typeof document !== 'undefined') {
      document.removeEventListener('mousemove', this.onGlobalMouseMove);
      document.removeEventListener('mouseup', this.onGlobalMouseUp);
      document.body.style.userSelect = '';
      document.body.style.cursor = '';
    }
  }

  /**
   * Get appropriate cursor for resize direction
   */
  private getResizeCursor(): string {
    const cursors: { [key: string]: string } = {
      'n': 'n-resize',
      's': 's-resize',
      'e': 'e-resize',
      'w': 'w-resize',
      'ne': 'ne-resize',
      'nw': 'nw-resize',
      'se': 'se-resize',
      'sw': 'sw-resize'
    };
    return cursors[this.resizeDirection] || 'default';
  }

  private getInteractionCursor(): string {
    if (this.isResizingPinnedRail) {
      return 'col-resize';
    }

    if (this.isResizing) {
      return this.getResizeCursor();
    }

    return 'grabbing';
  }

  private getInitialPinnedWidth(): number {
    if (!this.isBrowser()) {
      return 440;
    }

    const storedWidth = Number(localStorage.getItem(this.PINNED_WIDTH_STORAGE_KEY));
    if (!Number.isFinite(storedWidth) || storedWidth <= 0) {
      return this.getClampedPinnedWidth(440);
    }

    return this.getClampedPinnedWidth(storedWidth);
  }

  private getClampedPinnedWidth(width: number): number {
    if (!this.isBrowser()) {
      return Math.max(this.MIN_PINNED_WIDTH, Math.min(this.MAX_PINNED_WIDTH, width));
    }

    const viewportMax = Math.max(this.MIN_PINNED_WIDTH, Math.min(this.MAX_PINNED_WIDTH, Math.round(window.innerWidth * 0.55)));
    return Math.max(this.MIN_PINNED_WIDTH, Math.min(viewportMax, Math.round(width)));
  }

  private persistPinnedWidth(): void {
    if (!this.isBrowser()) {
      return;
    }

    localStorage.setItem(this.PINNED_WIDTH_STORAGE_KEY, String(this.pinnedWidth));
  }

  private syncPinnedWidthCss(): void {
    (this as { pinnedWidthCss: string }).pinnedWidthCss = `${this.pinnedWidth}px`;
  }

  private isBrowser(): boolean {
    return typeof window !== 'undefined' && typeof localStorage !== 'undefined';
  }

  /**
   * TrackBy function for ngFor to optimize rendering performance
   */
  trackByThreadId(index: number, thread: FloatingThreadState): string {
    return thread.threadId;
  }
}
